/* eslint-disable no-param-reassign */
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import actions from '@src/redux/actions';
import { useClickOutside } from '@src/hooks/useClickOutSide';
import { Fade, MenuItem, Paper, Popper } from '@mui/material';
import {
  FlagRounded,
  TuneRounded,
  PlayCircleFilledRounded,
} from '@mui/icons-material';
import PauseCircleFilledRoundedIcon from '@mui/icons-material/PauseCircleFilledRounded';
import apis from '@src/apis';
import { COLOR } from '@src/styles/color';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import MoreVertOutlinedIcon from '@mui/icons-material/MoreVertOutlined';
import { downloadFileUsingUrl } from '@src/utils/file';
import { AUDIO_EFFECTS_URL } from '@src/configs';
import { MAX_AUDIO_RETENTION_PERIOD } from '@src/constants';
// import { Box } from '@mui/material';
import {
  DOWNLOAD_QUOTA_EXCEEDED_CODE,
  SILENCE_AUDIO_URL,
} from '@src/constants/tts';
import { REQUEST_STATUS } from '@src/constants/voice';
import { trackAudioDownloaded, deriveModule } from '@src/services/moengage';
import { MOE_SURFACE } from '@src/constants/moengage';
// import Tooltip from '@src/components/Tooltip';
import useRequestAnimationStatus from '@src/hooks/useRequestAnimationStatus';
import { useCheckDubbingRoute } from '@src/hooks/useCheckDubbingRoute';
import { StyledIconButton, StyledActionTooltip } from './index.style';

const RequestAction = ({
  request,
  onOpenErrorReport,
  audioRef,
  playable,
  isDisabledErrorReport = false,
  hideEffectAudio = false,
}) => {
  const isDubbingRoute = useCheckDubbingRoute();
  const { t } = useTranslation();
  const {
    id: requestId,
    title,
    characters,
    voice,
    audioLink,
    retentionPeriod = MAX_AUDIO_RETENTION_PERIOD,
    createdAt,
    status,
    type,
    seconds,
    sentencesVoiceCode,
  } = request;

  const {
    audioLink: audioPlayerLink,
    isPlaying,
    duration,
  } = useSelector((state) => state.audioPlayer);

  const dispatch = useDispatch();
  const { displayStatus } = useRequestAnimationStatus(request);

  const handleOpenErrorReport = (e) => {
    e.stopPropagation();
    onOpenErrorReport({
      id: requestId,
      title,
      characters,
      voice,
      type,
      seconds,
      sentencesVoiceCode,
    });
  };

  const handleChangePlayAudio = (e) => {
    e.stopPropagation();
    if (audioLink && audioPlayerLink && audioPlayerLink === audioLink) {
      dispatch(actions.audioPlayer.updateStatus(!isPlaying));
      dispatch(actions.audioPlayer.updateShowAudioPlayer(true));
    } else {
      audioRef.current.src = SILENCE_AUDIO_URL;
      audioRef.current.load();
      audioRef.current.play();
      dispatch(actions.audioPlayer.updateAudioLink(audioLink));
      dispatch(actions.audioPlayer.updateSelectedAudioRequest(requestId));
      dispatch(actions.audioPlayer.updateStatus(true));
      dispatch(actions.audioPlayer.updateMetaData({ currentTime: 0 }));
      dispatch(actions.audioPlayer.updateShowAudioPlayer(true));
      dispatch(actions.audioPlayer.updatePreviewRequestInfo(request));
    }
  };

  const handleOpenAudioEffects = (e) => {
    e.stopPropagation();
    window.open(`${AUDIO_EFFECTS_URL}/?audio=${audioLink}`, '_blank');
  };

  const handleDownloadAudio = async (e) => {
    e.stopPropagation();
    const res = await apis.requests.getPresignedAudioUrl(requestId);
    if (res?.status) {
      const audioUrl = res.result?.audio;
      trackAudioDownloaded({
        requestId,
        module: deriveModule(request),
        surface: MOE_SURFACE.REQUEST_LIST,
      });
      downloadFileUsingUrl(audioUrl);
      return;
    }

    if (res?.errorCode === DOWNLOAD_QUOTA_EXCEEDED_CODE) {
      dispatch(actions.user.updateShowBlockDownloadDialog(true));
      return;
    }

    dispatch(
      actions.noti.push({
        severity: 'error',
        message: 'downloadError',
        value: title,
      }),
    );
  };

  const isShowPlaying =
    audioLink && audioPlayerLink === audioLink && isPlaying && !!duration;

  const audioExpired = moment().isAfter(
    moment(createdAt).add(retentionPeriod, 'days'),
  );
  const isDisabledAudio =
    !audioLink || audioExpired || displayStatus !== REQUEST_STATUS.SUCCESS;

  const getTitleActionTooltip = (action) => {
    // Request failed and processing
    if (status !== REQUEST_STATUS.SUCCESS) return t('');
    // Audio expired
    if (audioExpired) return t('notiAudioExpired', { retentionPeriod });

    return t(action);
  };

  const [anchorStatusRequest, setAnchorStatusRequest] = useState(null);
  const [openPopperStatusRequest, setOpenPopperStatusRequest] = useState(false);
  const refClickOutSide = useClickOutside(() => {
    setOpenPopperStatusRequest(false);
  });

  return (
    <div className="action-cell">
      {displayStatus === 'SUCCESS' && (
        <>
          {playable && !isDubbingRoute && (
            <StyledActionTooltip
              data-id="play-req-audio"
              data-request-id={request.id}
              title={getTitleActionTooltip('playAudio')}
              arrow
              placement="top"
              disabled={isDisabledAudio}
            >
              <span>
                <StyledIconButton
                  onClick={handleChangePlayAudio}
                  disabled={isDisabledAudio}
                  height="40px"
                  width="40px"
                  padding="12px"
                >
                  {isShowPlaying ? (
                    <PauseCircleFilledRoundedIcon
                      style={{ width: '24px' }}
                      className="icon"
                    />
                  ) : (
                    <PlayCircleFilledRounded
                      style={{
                        fontSize: '24px',
                      }}
                      className={
                        isDisabledAudio ? 'icon icon-disabled' : 'icon'
                      }
                    />
                  )}
                </StyledIconButton>
              </span>
            </StyledActionTooltip>
          )}
          <StyledActionTooltip
            data-id="download-req-audio"
            data-request-id={request.id}
            title={getTitleActionTooltip('download')}
            arrow
            placement="top"
            disabled={isDisabledAudio}
          >
            <span>
              <StyledIconButton
                onClick={handleDownloadAudio}
                disabled={isDisabledAudio}
                height="40px"
                width="40px"
                padding="12px"
              >
                <DownloadRoundedIcon
                  style={{ fontSize: '24px' }}
                  className={isDisabledAudio ? 'icon icon-disabled' : 'icon'}
                />
              </StyledIconButton>
            </span>
          </StyledActionTooltip>
        </>
      )}

      <div ref={refClickOutSide}>
        <StyledIconButton
          data-id="view-other-req-actions"
          onClick={(e) => {
            e.stopPropagation();
            setAnchorStatusRequest(e.currentTarget);
            setOpenPopperStatusRequest((prev) => !prev);
          }}
          height="40px"
          width="40px"
          padding="12px"
        >
          <MoreVertOutlinedIcon
            className="icon-morevert"
            color="rgba(30, 22, 78, 0.64)"
          />

          <Popper
            transition
            style={{ zIndex: 1000, width: '180px', color: '#1E164E' }}
            anchorEl={anchorStatusRequest}
            open={openPopperStatusRequest}
            placement="bottom-end"
          >
            {({ TransitionProps }) => (
              <Fade
                style={{ padding: '4px 0px' }}
                {...TransitionProps}
                timeout={350}
              >
                <Paper>
                  {!isDubbingRoute && !hideEffectAudio && (
                    <MenuItem
                      data-id="add-effect-req-audio"
                      data-request-id={requestId}
                      key="handleOpenNewAudioEffects"
                      onClick={handleOpenAudioEffects}
                      disabled={isDisabledAudio}
                      style={{
                        color: '#1E164E',
                        fontSize: '14px',
                        height: '40px',
                        boxSizing: 'border-box',
                        padding: '10px 16px 10px 16px',
                        fontWeight: 500,
                      }}
                    >
                      <>
                        {isDisabledAudio ? (
                          <span>
                            <StyledIconButton
                              disabled={isDisabledAudio}
                              height="40px"
                              isDisableHover
                              width="40px"
                              padding="12px"
                            >
                              <TuneRounded
                                style={{
                                  color: COLOR.darkBlue,
                                  fontSize: '24px',
                                }}
                                className="icon"
                              />
                            </StyledIconButton>
                          </span>
                        ) : (
                          <StyledIconButton
                            disabled={isDisabledAudio}
                            height="40px"
                            width="40px"
                            isDisableHover
                            padding="12px"
                          >
                            <TuneRounded
                              style={{
                                color: COLOR.darkBlue,
                                fontSize: '24px',
                              }}
                              className="icon"
                            />
                          </StyledIconButton>
                        )}
                      </>
                      <span className="option-title">{t('correction')}</span>
                    </MenuItem>
                  )}
                  <MenuItem
                    data-id="report-req"
                    data-request-id={requestId}
                    key="handleOpenErrorReport"
                    style={{
                      color: '#1E164E',
                      fontSize: '14px',
                      height: '40px',
                      boxSizing: 'border-box',
                      padding: '10px 16px 10px 16px',
                      fontWeight: 500,
                    }}
                    onClick={handleOpenErrorReport}
                    disabled={isDisabledErrorReport}
                  >
                    <StyledIconButton
                      height="40px"
                      width="40px"
                      padding="12px"
                      isDisableHover
                      disabled={isDisabledErrorReport}
                    >
                      <FlagRounded
                        style={{ color: '#FF3B30', fontSize: '24px' }}
                        className="icon"
                      />
                    </StyledIconButton>
                    <span className="option-title">{t('errorReport')}</span>
                  </MenuItem>
                </Paper>
              </Fade>
            )}
          </Popper>
        </StyledIconButton>
      </div>
    </div>
  );
};
export default RequestAction;
